import { useRouter } from "expo-router";
import {
  AlertCircle,
  BarChart3,
  Check,
  CheckCircle,
  Clock,
  Languages,
  MessageSquare,
  Rocket,
  Sparkles,
  Users,
} from "lucide-react-native";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";

import {
  CountriesProgress,
  CountryProgressItem,
  LanguageProgressItem,
  MapLoader,
  PersonasLoader,
  SummariesLoader,
  TranslationsProgress,
} from "@/components/runs";
import {
  Box,
  Card,
  Center,
  ErrorWithRetry,
  HStack,
  Progress,
  ProgressFilledTrack,
  Skeleton,
  Spinner,
  Text,
  VStack,
} from "@/components/ui";
import { currentLocale, useDatetimeUtils, useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { CountryValue } from "@/types/resources";
import { JobStep, ResourceType } from "@/types/utils";

interface RunMetadata {
  countries?: CountryValue[];
  steps: RunStep[];
}

interface RunProps {
  onComplete?: () => void;
  onRetry?: () => void;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  resourceId: string;
  resourceType: ResourceType;
  retryError?: string | null;
  retrying?: boolean;
  runId: string;
}

interface RunResult {
  message: string;
}

interface RunStep extends JobStep {
  progress?: StepProgress;
}

interface StepProgress {
  completed: number;
  countries?: CountryProgressItem[];
  failed?: number;
  languages?: LanguageProgressItem[];
  progress: number;
  sub_completed?: number;
  subtotal?: number;
  total: number;
}

// Gather respondents, Prepare study, Launch simulation, Run study, Process
// results, Generate summaries
const STEP_ICONS = [
  Users,
  Languages,
  Rocket,
  MessageSquare,
  BarChart3,
  Sparkles,
];

/* ── Skeleton ───────────────────────────────────────────── */

export const RunSkeleton: FC = () => (
  <ScrollView className="flex-1" contentContainerClassName="py-6 gap-5">
    <VStack className="flex-1 gap-5">
      <VStack className="gap-2">
        <HStack className="justify-between items-center">
          <Skeleton className="h-4 w-40 rounded" />
          <Skeleton className="h-4 w-24 rounded" />
        </HStack>
        <Skeleton className="h-2 w-full rounded-full" />
      </VStack>
      <HStack className="gap-10 items-start">
        <VStack className="flex-1 gap-1">
          {[...Array(6)].map((_, i) => (
            <HStack key={i} className="items-center gap-3 px-4 py-3">
              <Skeleton className="w-8 h-8 rounded-lg shrink-0" />
              <Skeleton className="h-4 flex-1 rounded" />
            </HStack>
          ))}
        </VStack>
        <Skeleton className="flex-[3] min-h-[320px] rounded-2xl" />
      </HStack>
    </VStack>
  </ScrollView>
);

/* ── Sub-components ─────────────────────────────────────── */

type CompletedCardProps = { label: string; sublabel: string };

const CompletedCard: FC<CompletedCardProps> = ({ label, sublabel }) => (
  <Card className="border border-outline-200 rounded-2xl p-8 min-h-[480px] animate-slide-in-right">
    <Center className="flex-1">
      <Box className="w-16 h-16 rounded-2xl bg-primary-50 items-center justify-center mb-4">
        <CheckCircle className="text-primary-500" size={32} strokeWidth={2} />
      </Box>
      <Text className="text-base font-semibold text-typography-900 mb-1">
        {label}
      </Text>
      <Text className="text-sm text-typography-500">{sublabel}</Text>
    </Center>
  </Card>
);

const Run: FC<RunProps> = ({
  onComplete,
  onRetry,
  onScroll,
  resourceId,
  resourceType,
  retryError,
  retrying,
  runId,
}) => {
  const { i18n } = useLocalization();
  const router = useRouter();
  const { formatDuration } = useDatetimeUtils();

  const {
    error: pollingError,
    jobData,
    polling,
  } = API.Jobs.usePollJob<RunResult, RunMetadata>(runId);

  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Animation for sparkles icon
  const scale = useSharedValue(1);

  useEffect(() => {
    scale.value = withRepeat(
      withTiming(1.2, {
        duration: 1000,
        easing: Easing.inOut(Easing.ease),
      }),
      -1,
      true
    );
  }, [scale]);

  const sparklesAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  // Timer for elapsed time
  useEffect(() => {
    if (
      !polling ||
      !jobData ||
      !jobData.started_at ||
      jobData.status === "queued" ||
      jobData.status === "finished" ||
      jobData.status === "failed"
    ) {
      return;
    }

    const jobStartTime = new Date(jobData.started_at).getTime();

    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - jobStartTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [polling, jobData]);

  const handleRetryPolling = () => {
    if (resourceType === "study") {
      router.replace({
        params: { studyId: resourceId },
        pathname: `/studies/${resourceId}`,
      });
    } else {
      router.replace({
        params: { questionId: resourceId },
        pathname: `/adhoc/questions/${resourceId}`,
      });
    }
  };

  const fmt = (n: number) =>
    i18n.numberToDelimited(n, {
      delimiter: currentLocale.digitGroupingSeparator ?? undefined,
    });

  const getProgressLabel = (stepIndex: number, progress: StepProgress) => {
    const { completed, sub_completed, subtotal, total } = progress;

    switch (stepIndex) {
      case 0:
        return i18n.t("studies.respondentsProgress", {
          completed: fmt(completed),
          count: total,
        });
      case 1:
        return i18n.t("studies.elementsProgress", {
          completed: fmt(completed),
          count: total,
        });
      case 3: {
        const respondents = i18n.t("studies.respondentsProgress", {
          completed: fmt(completed),
          count: total,
        });
        if (sub_completed !== undefined && subtotal !== undefined) {
          const answers = i18n.t("studies.answersProgress", {
            completed: fmt(sub_completed),
            count: subtotal,
          });
          return `${respondents} (${answers})`;
        }
        return respondents;
      }
      case 4:
        return i18n.t("studies.answersProgress", {
          completed: fmt(completed),
          count: total,
        });
      case 5:
        return i18n.t("studies.summariesProgress", {
          completed: fmt(completed),
          count: total,
        });
      default:
        return `${fmt(completed)}/${fmt(total)}`;
    }
  };

  // `Date.now()` is impure and can't be called during render. Keep the current
  // time in state, ticking every second, so the live "time remaining" estimate
  // keeps counting down between job-status polls.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate overall progress and estimated time
  const { estimatedTimeRemaining, overallProgress } = useMemo(() => {
    if (!jobData?.metadata.steps || jobData.status === "queued") {
      return { estimatedTimeRemaining: null, overallProgress: 0 };
    }

    const steps = jobData.metadata.steps;

    // A started step blends the countdown on its learned estimate with the
    // observed pace `e * (1 - p) / p`, weighted p^2 so early, noisy progress
    // does not swamp the estimate.
    const totalRemaining = steps.reduce((sum, step) => {
      if (step.status === "finished") return sum;
      if (step.status !== "started") return sum + step.estimated_seconds;

      const p = step.progress?.progress ?? 0;
      const e = (now - new Date(step.started_at!).getTime()) / 1000;
      return (
        sum +
        (1 - p * p) * Math.max(step.estimated_seconds - e, 0) +
        p * (1 - p) * e
      );
    }, 0);

    // Calculate progress with proper weights
    // Questions: 80%, Summary: 15%, Rest: 5%
    const SIMULATION_WEIGHT = 0.8;
    const SUMMARY_WEIGHT = 0.15;
    const OTHER_WEIGHT = 0.05;

    let progressValue = 0;

    steps.forEach((step, idx) => {
      const stepProgress = step.progress?.progress || 0;

      if (idx === 3) {
        // Simulation step - 80%
        if (step.status === "finished") {
          progressValue += SIMULATION_WEIGHT;
        } else if (step.status === "started") {
          progressValue += SIMULATION_WEIGHT * stepProgress;
        }
      } else if (idx === 5) {
        // Summary step - 15%
        if (step.status === "finished") {
          progressValue += SUMMARY_WEIGHT;
        } else if (step.status === "started") {
          progressValue += SUMMARY_WEIGHT * stepProgress;
        }
      } else {
        // Other steps - 5% total
        const numOtherSteps = steps.length - 2;
        const stepWeight = OTHER_WEIGHT / numOtherSteps;

        if (step.status === "finished") {
          progressValue += stepWeight;
        } else if (step.status === "started") {
          progressValue += stepWeight * stepProgress;
        }
      }
    });

    return {
      estimatedTimeRemaining: Math.max(0, Math.ceil(totalRemaining)),
      overallProgress: progressValue,
    };
  }, [jobData, now]);

  const completed = useMemo(
    () => jobData?.status === "finished",
    [jobData?.status]
  );

  useEffect(() => {
    if (completed) onComplete?.();
  }, [completed, onComplete]);

  const [displayedStepIdx, setDisplayedStepIdx] = useState(-1);
  const [displayedSteps, setDisplayedSteps] = useState<RunStep[] | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const prevActiveStepIdxRef = useRef(-1);

  // Reset all step-transition state when runId changes (e.g. after retry).
  // State resets run during render (not in an effect); the ref reset stays in
  // an effect (ref writes during render are disallowed) and runs before the
  // step-transition effect below.
  const [prevRunId, setPrevRunId] = useState(runId);
  if (prevRunId !== runId) {
    setPrevRunId(runId);
    setDisplayedStepIdx(-1);
    setDisplayedSteps(null);
    setShowCompleted(false);
  }

  useEffect(() => {
    prevActiveStepIdxRef.current = -1;
  }, [runId]);

  const steps = jobData?.metadata.steps;
  const activeStepIdx = steps?.findIndex((s) => s.status === "started") ?? -1;

  useEffect(() => {
    if (jobData?.status === "failed") return;

    if (activeStepIdx === -1) {
      // All steps done — linger 1500ms on the last step before showing completed
      if (prevActiveStepIdxRef.current !== -1) {
        prevActiveStepIdxRef.current = -1;
        const timer = setTimeout(() => setShowCompleted(true), 1500);
        return () => clearTimeout(timer);
      }
      return;
    }

    if (prevActiveStepIdxRef.current === -1) {
      // First active step — show immediately, snapshot all step names
      setDisplayedStepIdx(activeStepIdx);
      setDisplayedSteps([...steps!]);
      prevActiveStepIdxRef.current = activeStepIdx;
    } else if (activeStepIdx !== prevActiveStepIdxRef.current) {
      // Step changed — wait 1500ms then advance (old step shows at 100% during freeze)
      prevActiveStepIdxRef.current = activeStepIdx;
      const timer = setTimeout(() => {
        setDisplayedStepIdx(activeStepIdx);
        setDisplayedSteps([...steps!]);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [activeStepIdx, jobData?.status, steps]);

  if (!jobData && !pollingError) return <RunSkeleton />;

  const showProgressBar =
    jobData && jobData.status !== "queued" && jobData.status !== "failed";

  const displayedStep = displayedStepIdx >= 0 ? steps![displayedStepIdx] : null;

  return (
    <ScrollView
      className="flex-1"
      contentContainerClassName="py-6 gap-5"
      onScroll={onScroll}
      scrollEventThrottle={16}
    >
      <VStack className="flex-1 gap-5">
        {/* Overall Progress Bar */}
        {showProgressBar && (
          <VStack className="gap-2">
            <HStack className="justify-between items-center">
              <HStack className="items-center gap-2">
                <Animated.View style={sparklesAnimatedStyle}>
                  <Sparkles
                    className="text-primary-500"
                    size={15}
                    strokeWidth={2.5}
                  />
                </Animated.View>
                <Text className="text-sm font-semibold text-typography-900">
                  {estimatedTimeRemaining !== null &&
                  estimatedTimeRemaining < 120
                    ? i18n.t("studies.workingOnItMessageAlmostReady")
                    : i18n.t("studies.workingOnItMessage")}
                </Text>
              </HStack>
              <HStack className="items-center gap-3">
                <Text className="text-xs text-typography-500">
                  {i18n.t("studies.timeElapsed", {
                    time: formatDuration(elapsedSeconds),
                  })}
                </Text>
                {estimatedTimeRemaining !== null && (
                  <Text className="text-xs text-typography-500">
                    {i18n.t("studies.timeRemaining", {
                      time: formatDuration(estimatedTimeRemaining),
                    })}
                  </Text>
                )}
                <Text className="text-sm font-bold text-primary-500">
                  {i18n.numberToPercentage(overallProgress * 100, {
                    delimiter: currentLocale.decimalSeparator ?? undefined,
                    precision: 0,
                  })}
                </Text>
              </HStack>
            </HStack>
            <Progress
              className="w-full h-2 bg-background-100"
              size="sm"
              value={overallProgress * 100}
            >
              <ProgressFilledTrack className="animate-shimmer bg-gradient-to-r from-tertiary-500 via-primary-400 to-tertiary-500 [transition:width_0.6s_ease-out]" />
            </Progress>
          </VStack>
        )}

        {/* Queued State */}
        {polling && jobData?.status === "queued" && (
          <Card className="border border-outline-200 rounded-2xl p-4">
            <HStack className="items-center gap-3">
              <Box className="w-9 h-9 rounded-xl bg-background-100 items-center justify-center">
                <Clock
                  className="text-typography-400"
                  size={18}
                  strokeWidth={2}
                />
              </Box>
              <VStack className="flex-1">
                <Text className="text-sm font-bold text-typography-900">
                  {i18n.t("studies.runWaitingToStart")}
                </Text>
                <Text className="text-xs text-typography-500 mt-0.5">
                  {i18n.t("studies.runInQueue")}
                </Text>
              </VStack>
            </HStack>
          </Card>
        )}

        {/* Sidebar + Active Step Panel */}
        {(polling || completed) &&
          steps &&
          (jobData?.status === "started" || completed) && (
            <HStack className="gap-6 lg:gap-10 items-start flex-col lg:flex-row">
              {/* Step sidebar */}
              <VStack className="w-full lg:flex-1 gap-1">
                {steps.map((step, idx) => {
                  const isFinished = showCompleted
                    ? step.status === "finished"
                    : idx < displayedStepIdx;
                  const isStarted = !isFinished && idx === displayedStepIdx;
                  const Icon = STEP_ICONS[idx] ?? Clock;
                  return (
                    <HStack
                      key={idx}
                      className={`items-center gap-3 rounded-xl px-4 py-3 ${
                        isStarted
                          ? "bg-primary-50 border border-primary-100/60"
                          : "bg-transparent"
                      }`}
                    >
                      <Box
                        className={`w-8 h-8 rounded-lg items-center justify-center shrink-0 ${
                          isFinished
                            ? "bg-primary-500"
                            : isStarted
                              ? "bg-typography-900"
                              : "bg-background-200"
                        }`}
                      >
                        {isFinished ? (
                          <Check color="white" size={14} strokeWidth={2.5} />
                        ) : (
                          <Icon
                            color={
                              isStarted
                                ? "white"
                                : "rgb(var(--color-typography-500))"
                            }
                            size={14}
                            strokeWidth={2}
                          />
                        )}
                      </Box>
                      <VStack className="flex-1">
                        <Text
                          className={`text-sm font-medium ${
                            isFinished
                              ? "text-primary-600"
                              : isStarted
                                ? "text-typography-900"
                                : "text-typography-500"
                          }`}
                          numberOfLines={1}
                        >
                          {displayedSteps?.[idx]?.step_name ?? step.step_name}
                        </Text>
                      </VStack>
                    </HStack>
                  );
                })}
              </VStack>

              {/* Active step panel */}
              <Box className="w-full lg:flex-[3]">
                {showCompleted ? (
                  <CompletedCard
                    label={i18n.t("misc.completed")}
                    sublabel={i18n.t("studies.redirectingToResults")}
                  />
                ) : !displayedStep ? (
                  <Card className="border border-outline-200 rounded-2xl p-8 min-h-[320px]">
                    <HStack className="items-center gap-3 mb-6">
                      <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
                      <VStack className="flex-1 gap-1.5">
                        <Skeleton className="h-4 w-36 rounded" />
                        <Skeleton className="h-3.5 w-24 rounded" />
                      </VStack>
                    </HStack>
                    <Skeleton className="flex-1 min-h-[200px] rounded-xl" />
                  </Card>
                ) : (
                  <Card className="border border-outline-200 rounded-2xl p-8 min-h-[320px]">
                    <HStack className="items-center gap-3 mb-6">
                      <Box className="w-10 h-10 rounded-xl bg-primary-50 items-center justify-center">
                        {(() => {
                          const Icon = STEP_ICONS[displayedStepIdx] ?? Clock;
                          return (
                            <Icon
                              className="text-primary-600"
                              size={18}
                              strokeWidth={2}
                            />
                          );
                        })()}
                      </Box>
                      <VStack className="flex-1">
                        <Text className="text-base font-semibold text-typography-900">
                          {displayedSteps?.[displayedStepIdx]?.step_name ??
                            displayedStep.step_name}
                        </Text>
                        {displayedStepIdx !== 2 &&
                          (displayedStep.progress?.total ? (
                            <Text className="text-sm text-typography-500">
                              {getProgressLabel(
                                displayedStepIdx,
                                displayedStep.progress
                              )}
                            </Text>
                          ) : (
                            <Skeleton className="h-3.5 w-28 rounded mt-0.5" />
                          ))}
                      </VStack>
                      <Spinner
                        color="rgb(var(--color-primary-500))"
                        size="small"
                      />
                    </HStack>

                    {(() => {
                      const idx = displayedStepIdx;
                      const prog = displayedStep.progress;

                      // Generic shimmer progress bar (used as fallback)
                      const genericProgress = prog ? (
                        <VStack className="gap-2">
                          <Progress
                            className="w-full h-1.5 bg-background-200"
                            size="sm"
                            value={prog.progress * 100}
                          >
                            <ProgressFilledTrack className="animate-shimmer bg-gradient-to-r from-tertiary-500 via-primary-400 to-tertiary-500 [transition:width_0.6s_ease-out]" />
                          </Progress>
                          <HStack className="justify-between items-center">
                            <Text className="text-xs text-typography-600 flex-1 mr-2">
                              {getProgressLabel(idx, prog)}
                            </Text>
                            <HStack className="items-center gap-2">
                              {(prog.failed ?? 0) > 0 && (
                                <HStack className="items-center gap-1">
                                  <AlertCircle
                                    color="rgb(var(--color-warning-500))"
                                    size={12}
                                    strokeWidth={2.5}
                                  />
                                  <Text className="text-xs font-bold text-warning-600">
                                    {prog.failed}
                                  </Text>
                                </HStack>
                              )}
                              <Text className="text-xs font-bold text-primary-500">
                                {i18n.numberToPercentage(prog.progress * 100, {
                                  delimiter:
                                    currentLocale.decimalSeparator ?? undefined,
                                  precision: 0,
                                })}
                              </Text>
                            </HStack>
                          </HStack>
                        </VStack>
                      ) : (
                        <Text className="text-xs text-typography-500">
                          {i18n.t("studies.waitingForProgress")}
                        </Text>
                      );

                      // Step 0: Gather respondents – animated persona cards
                      if (idx === 0)
                        return (
                          <PersonasLoader
                            countries={jobData?.metadata.countries ?? []}
                          />
                        );

                      // Step 1: Prepare study (translations) – per-language progress
                      if (idx === 1) {
                        if (prog?.languages)
                          return (
                            <TranslationsProgress languages={prog.languages} />
                          );
                        return genericProgress;
                      }

                      // Step 2: Launch simulation – world map of targeted countries
                      if (idx === 2)
                        return (
                          <MapLoader
                            countries={jobData?.metadata.countries ?? []}
                          />
                        );

                      // Step 3: Run study – countries simulation bars
                      if (idx === 3) {
                        if (prog?.countries)
                          return (
                            <CountriesProgress countries={prog.countries} />
                          );
                        return genericProgress;
                      }

                      // Step 4: Process results – per-language progress bars
                      if (idx === 4) {
                        if (prog?.languages)
                          return (
                            <TranslationsProgress languages={prog.languages} />
                          );
                        return genericProgress;
                      }

                      // Step 5: Generate summaries – insights visual
                      if (idx === 5)
                        return (
                          <SummariesLoader
                            progress={prog?.progress ?? 0}
                            total={prog?.total ?? 0}
                          />
                        );

                      return genericProgress;
                    })()}
                  </Card>
                )}
              </Box>
            </HStack>
          )}

        {/* Polling Error */}
        {pollingError && (
          <ErrorWithRetry
            error={pollingError}
            onRetry={handleRetryPolling}
            title={i18n.t("studies.errorGettingSimulationProgress")}
          />
        )}

        {/* Job Failed */}
        {jobData?.status === "failed" && onRetry && (
          <ErrorWithRetry
            error={retryError || i18n.t("misc.errorOccurred")}
            onRetry={onRetry}
            retrying={retrying}
            title={i18n.t("studies.errorRunningSimulation")}
          />
        )}
      </VStack>
    </ScrollView>
  );
};

export default Run;
